import { useState, useEffect, useRef } from 'react';
import { isEffectivelyComplete } from '@harvus/shared';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { onTasksChanged } from '../../utils/taskEvents';
import { Btn, Modal, Spinner, StatusBadge, Badge, InfoGrid, SectionHeader, useToast, Icon, clickable } from '../ui';
import { TaskEditModal } from './TaskEditModal';
import { NotesFeed } from './Notes';
import { EDITABLE_STATUSES, STATUS_COLORS, StatusPill, Avatar, TaskRow, QuickNoteComposer, completeRefreshDelay } from '../tasks/TaskComponents';
import { TaskCreateModal } from '../tasks/TaskCreateModal';

// Reusable Tasks panel for the deal / deal-detail screen. Mirrors the main
// Tasks page list view (subtasks, inline status, quick notes, full detail
// modal with notes + messages) but scoped to a single deal / deal detail.
// Pass dealDetailId to scope to a single detail; omit it for deal-level tasks.
export const DealTasksPanel = ({ dealId, dealDetailId, users = [], includeDealLevel = false, onChanged }) => {
  const { toast } = useToast();
  // Notify the parent (combined Notes & Tasks panel) whenever a task write
  // should refresh the shared notes+tasks summary. reload() refetches the list.
  const notifyChanged = () => onChanged?.();
  // In a unified (single-detail) deal view, includeDealLevel folds deal-level
  // tasks into the detail's tab (mirrors NotesFeed) so a deal-level task — e.g.
  // one Harvi created — isn't hidden.
  const params = dealDetailId
    ? `dealId=${dealId}&dealDetailId=${dealDetailId}${includeDealLevel ? '&includeDealLevel=true' : ''}`
    : `dealId=${dealId}`;
  const { data: tasks, loading, reload } = useApi(() => api.get(`tasks?${params}`), [dealId, dealDetailId, includeDealLevel]);
  const { data: contacts } = useApi(() => api.get('contacts'), []);
  // A task added from a note's suggestions (or the proposals pop-up) was
  // created outside this panel — refetch so it appears without a page reload.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => onTasksChanged(() => reloadRef.current?.()), []);

  const [showModal, setShowModal] = useState(false);   // new / subtask modal
  const [addingChildFor, setAddingChildFor] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [expanded,  setExpanded]  = useState({});       // subtask expand state

  // Detail modal state
  const [selected,    setSelected]    = useState(null);
  const [showEditTask, setShowEditTask] = useState(false);
  const [messages,    setMessages]    = useState({});
  const [newMsg,      setNewMsg]      = useState('');
  const [mentionedUserIDs, setMentionedUserIDs] = useState([]);
  const [mentionQuery,     setMentionQuery]     = useState(null);

  // Quick-note composer state — which task row currently has the composer open.
  const [quickNoteFor, setQuickNoteFor] = useState(null);


  const toggleExpand = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

  // ---- Quick notes -----------------------------------------------------------
  const openQuickNote = (t) => setQuickNoteFor(t.taskID);

  // ---- Status / completion ---------------------------------------------------
  const changeStatus = async (t, status) => {
    try {
      await api.put(`tasks/${t.taskID}/status`, { status });
      await completeRefreshDelay(status);
      reload(); notifyChanged();
      return true;
    } catch (e) { toast.error(e.message); return false; }
  };

  // ---- Detail modal + messages -----------------------------------------------
  const loadMessages = async (taskID) => {
    if (messages[taskID]) return;
    try { const msgs = await api.get(`tasks/${taskID}/messages`); setMessages(p => ({ ...p, [taskID]: msgs })); }
    catch {}
  };

  const openDetail = (t) => { setSelected(t); loadMessages(t.taskID); };

  const onMsgChange = (e) => {
    const val = e.target.value;
    setNewMsg(val);
    const lastAt = val.lastIndexOf('@');
    if (lastAt >= 0 && lastAt === val.length - 1) {
      setMentionQuery('');
    } else if (lastAt >= 0 && !val.slice(lastAt + 1).includes(' ')) {
      setMentionQuery(val.slice(lastAt + 1).toLowerCase());
    } else {
      setMentionQuery(null);
    }
  };

  const pickMention = (user) => {
    const lastAt = newMsg.lastIndexOf('@');
    const before = newMsg.slice(0, lastAt);
    setNewMsg(`${before}@${user.displayName} `);
    setMentionedUserIDs(p => p.includes(user.userID) ? p : [...p, user.userID]);
    setMentionQuery(null);
  };

  const sendMsg = async () => {
    if (!newMsg.trim() || !selected) return;
    try {
      const m = await api.post(`tasks/${selected.taskID}/messages`, { content: newMsg.trim(), mentionedUserIDs });
      setMessages(p => ({ ...p, [selected.taskID]: [...(p[selected.taskID] || []), m] }));
      setNewMsg(''); setMentionedUserIDs([]); setMentionQuery(null);
      notifyChanged();
    } catch (e) { toast.error(e.message); }
  };

  // ---- Create / edit / delete ------------------------------------------------

  const openNew = () => { setAddingChildFor(null); setShowModal(true); };

  // Add Subtask on a top-level row — the shared modal seeds its own parent
  // field from the task object, so this is state only.
  const openAddChild = (parentTask) => {
    setAddingChildFor(parentTask);
    setShowModal(true);
  };

  // Editing goes through the shared TaskEditModal (same editor as the Tasks
  // table / dashboard) so the edit screen is identical everywhere.
  const openEdit = () => setShowEditTask(true);

  if (loading) return <Spinner />;

  const all            = tasks || [];
  // Effective completion (shared/src/taskTree.ts): a group is done when its
  // subtasks are, and is never a task of its own to count.
  const completedCount = all.filter(t => isEffectivelyComplete(t)).length;

  const childrenOf = (taskID) => {
    const children = all.filter(t => t.parentTaskID === taskID && (showCompleted || !isEffectivelyComplete(t)));
    return children.sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });
  };

  // Top-level rows: tasks without a parent (subtasks render nested under them).
  const topLevel = all
    .filter(t => !t.parentTaskID && (showCompleted || !isEffectivelyComplete(t)));

  const renderRows = (taskList, depth = 0) => taskList.map(task => {
    const isExpanded = expanded[task.taskID];
    const children   = childrenOf(task.taskID);
    return (
      <div key={task.taskID}>
        <TaskRow task={task} depth={depth} expanded={isExpanded} onToggle={toggleExpand}
          onOpen={openDetail} onAddChild={depth === 0 ? openAddChild : null}
          onStatusChange={changeStatus} onQuickNote={openQuickNote} visibleChildCount={children.length}
          fadeOnComplete={!showCompleted} />
        {quickNoteFor === task.taskID && (
          <QuickNoteComposer taskId={task.taskID} taskTitle={task.title} onClose={() => setQuickNoteFor(null)} onSaved={notifyChanged} />
        )}
        {isExpanded && children.length > 0 && renderRows(children, depth + 1)}
      </div>
    );
  });

  // Parent-task options for the edit / new modal (top-level tasks in this scope).
  const parentTaskItems = (excludeId) => all
    .filter(t => !t.parentTaskID && t.taskID !== excludeId)
    .map(t => ({ value: t.taskID, label: t.title }));

  const taskMsgs = selected ? (messages[selected.taskID] || []) : [];

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        {completedCount > 0 ? (
          <button onClick={() => setShowCompleted(v => !v)}
            style={{
              padding: '4px 12px', borderRadius: 20,
              border: `1px solid ${showCompleted ? B.tealDk : B.border}`,
              background: showCompleted ? B.tealLt : B.white,
              color: showCompleted ? B.tealDk : B.textMd,
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            }}>
            {showCompleted ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={13} /> Showing completed</span> : `Show completed (${completedCount})`}
          </button>
        ) : <span />}
        <Btn size="sm" onClick={openNew}>+ Add Task</Btn>
      </div>

      {topLevel.length === 0 ? (
        <div style={{ color: B.textLt, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          {all.length > 0 ? 'All tasks completed.' : 'No tasks yet. Click “+ Add Task” to create one.'}
        </div>
      ) : (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {renderRows(topLevel)}
        </div>
      )}

      {/* ---- Detail modal: status, notes, messages ---- */}
      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={680}
          headerAction={<Btn size="sm" variant="secondary" onClick={() => openEdit(selected)}>Edit</Btn>}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatusPill status={selected.status} />
            <StatusBadge status={selected.priority} />
            {!!selected.isBlocked && <Badge color={B.amber}>Blocked</Badge>}
          </div>
          <InfoGrid items={[
            ['Due Date', fmt.date(selected.dueDate)],
            ['Assigned', selected.assigneeNames],
            ...(selected.outsidePartyNames ? [['Outside Parties', selected.outsidePartyNames]] : []),
            ...(selected.isCompleted && selected.completedByName ? [['Completed By', selected.completedByName]] : []),
          ]} />
          {selected.sourceNoteID && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: B.textMd, marginBottom: 12, padding: '6px 10px', background: B.surfaceMuted, borderRadius: 6, border: `1px solid ${B.border}` }}>
              <Icon name="note" size={13} /><span>Promoted from a note</span>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.textMd, display: 'block', marginBottom: 6, letterSpacing: 0.5 }}>CHANGE STATUS</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {EDITABLE_STATUSES.map(s => (
                <button key={s} onClick={async () => { await api.put(`tasks/${selected.taskID}/status`, { status: s }); setSelected(p => ({ ...p, status: s, isCompleted: s === 'Complete' })); reload(); notifyChanged(); }}
                  style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${STATUS_COLORS[s]}`, background: selected.status === s ? STATUS_COLORS[s] : '#fff', color: selected.status === s ? '#fff' : STATUS_COLORS[s], fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{s}</button>
              ))}
            </div>
          </div>
          <SectionHeader title="Notes" />
          <NotesFeed taskId={selected.taskID} users={users} />
          {/* A task's conversation is its notes (migration 236 copied every
              message into a task note); this shows only messages not copied
              yet, on a database behind 236. */}
          {taskMsgs.length > 0 && (<>
          <SectionHeader title="Messages" />
          <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, overflow: 'visible', marginBottom: 12 }}>
            <div style={{ maxHeight: 220, overflowY: 'auto', padding: 12 }}>
              {taskMsgs.length === 0 && <div style={{ fontSize: 13, color: B.textLt, textAlign: 'center', padding: 16 }}>No messages yet.</div>}
              {taskMsgs.map((m, i) => (
                <div key={m.messageID ?? i} style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, background: B.surfaceMuted }}>
                  <div style={{ fontSize: 11, color: B.textLt, marginBottom: 2 }}>{m.sentByName} · {fmt.date(m.sentUTC)}</div>
                  <div style={{ fontSize: 13, color: B.text, whiteSpace: 'pre-wrap' }}>{m.content}</div>
                </div>
              ))}
            </div>
            {mentionQuery !== null && (
              <div style={{ borderTop: `1px solid ${B.border}`, maxHeight: 160, overflowY: 'auto', background: '#fff' }}>
                <div style={{ padding: '4px 12px', fontSize: 11, color: B.textLt, fontWeight: 600, letterSpacing: 0.5, borderBottom: `1px solid ${B.border}` }}>MENTION A USER</div>
                {(users || []).filter(u => u.displayName?.toLowerCase().includes(mentionQuery)).map(u => (
                  <div key={u.userID} {...clickable(() => pickMention(u), { role: 'option' })}
                    style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <Avatar name={u.displayName} size={22} />
                    <span>{u.displayName}</span>
                  </div>
                ))}
                {(users || []).filter(u => u.displayName?.toLowerCase().includes(mentionQuery)).length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: B.textLt }}>No users found</div>}
              </div>
            )}
            <div style={{ borderTop: `1px solid ${B.border}`, display: 'flex', gap: 8, padding: 10, alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input value={newMsg} onChange={onMsgChange} placeholder="Type a message… use @ to mention someone"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) { e.preventDefault(); sendMsg(); } if (e.key === 'Escape') setMentionQuery(null); }}
                  style={{ width: '100%', padding: '8px 12px', border: `1px solid ${B.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>
              <Btn size="sm" onClick={sendMsg}>Send</Btn>
            </div>
          </div>
          </>)}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="ghost" onClick={() => setSelected(null)}>Close</Btn>
          </div>
        </Modal>
      )}

      {/* ---- Edit modal (shared editor — identical to the Tasks table) ---- */}
      {showEditTask && selected && (
        <TaskEditModal taskId={selected.taskID} users={users}
          onClose={() => setShowEditTask(false)}
          onSaved={() => { setShowEditTask(false); setSelected(null); reload(); notifyChanged(); }} />
      )}

      {/* ---- New / subtask modal — the shared form every surface creates through ---- */}
      {showModal && (
        <TaskCreateModal
          fixedScope
          dealId={dealId}
          dealDetailId={dealDetailId}
          parentTask={addingChildFor}
          onClearParent={() => setAddingChildFor(null)}
          parentOptions={parentTaskItems(null)}
          users={users}
          contacts={contacts || []}
          onClose={() => { setShowModal(false); setAddingChildFor(null); }}
          onCreated={() => { reload(); notifyChanged(); }}
        />
      )}
    </div>
  );
};
